import { MessageService, ConfirmationService } from 'primeng/api'
import { Component, OnDestroy, OnInit } from '@angular/core'
import { Subscription } from 'rxjs'

import { LEASE_STATUS_COLOR_ENUM } from '@modules/private/lease/data/enum/lease.enum'
import { IUserAuthenticated } from '@data/interfaces/iuser-authenticated.interface'
import { LeaseDetailService } from '../../data/services/view/lease-detail.service'
import { APPLICATION_WARNING_LABELS } from '@modules/private/application-messages'
import { CommentsService } from '../../data/services/view/comments.service'

/**
 * Bitácora del arrendamiento. Reúne DOS mecanismos distintos (ver `api-rendafin/.claude/audit-trail.md`):
 *  - Historial de cambios (auditTrail[]): registro AUTOMÁTICO e inmutable de acciones sensibles (timeline, solo lectura).
 *  - Comentarios (comments[]): notas HUMANAS intencionales (alta/baja). La gestión vive AQUÍ (no en Cobranza).
 */
@Component({
  selector: 'app-lease-activity',
  templateUrl: './lease-activity.component.html',
  styleUrls: ['./lease-activity.component.scss'],
  providers: [MessageService, ConfirmationService],
})
export class LeaseActivityComponent implements OnInit, OnDestroy {
  // Etiquetas legibles por tipo de acción de auditoría. Agregar aquí al sumar nuevas acciones.
  actionLabels: Record<string, string> = {
    AMORTIZATION_START_DATE_CHANGED: 'Fecha de inicio de amortización recorrida',
    ASSET_ASSIGNED: 'Activo real asignado',
    //* Plan v4.3 §7: el activo asignado es de otra categoría que el tipo que eligió el cliente (no bloquea).
    ASSET_TYPE_MISMATCH: 'Activo de otra categoría que el tipo elegido',
    LEASE_AMOUNTS_EDIT_PERIODS: 'Montos de letras editados',
    LEASE_AMOUNTS_EDIT_LEASE_TOTALS: 'Totales de cartera ajustados a mano',
    LEASE_AMOUNTS_EARLY_TERMINATION: 'Terminación anticipada',
    LEASE_AMOUNTS_WRITE_OFF_RESIDUAL: 'Diferencias menores condonadas',
    LEASE_AMOUNTS_ZERO_PERIOD_PAYMENT: 'Saldo esperado de letras cubiertas puesto en cero',
    LEASE_AMOUNTS_RECOMPUTE: 'Saldos recalculados desde la tabla',
    LEASE_AMOUNTS_REVERT: 'Edición de saldos deshecha',
    LEASE_AMOUNTS_WAIVE_MORA: 'Mora condonada',
    MORATORY_RATE_CHANGED: 'Tasa moratoria del arrendamiento cambiada',
    LEASE_PERIODS_RENUMBERED: 'Letras renumeradas',
    LEASE_PAYMENT_APPLIED: 'Pago aplicado',
    LEASE_APPLICATION_CANCELLED: 'Aplicación cancelada',
    LEASE_PAYMENT_VOIDED: 'Pago anulado',
    LEASE_PAYMENT_DATE_CHANGED: 'Fecha de pago corregida',
    //* B-012: el monto capturado del pago estaba mal (`metadata`: previousAmount, newAmount).
    LEASE_PAYMENT_AMOUNT_CORRECTED: 'Monto del pago corregido',
    LEASE_STATUS_CHANGED: 'Estado del arrendamiento actualizado',
    //* F-142: la sesión del comité decidió sobre este arrendamiento (`metadata`: sessionId, meetingId, kind, decision).
    COMMITTEE_DECISION_RECORDED: 'Decisión del comité registrada',
    LEASE_RESIDUAL_CORRECTED: 'Residual corregido',
    LEASE_WRITE_OFF_RECORDED: 'Condonación de la terminación registrada',
    LEASE_WRITE_OFF_REVERTED: 'Condonación de la terminación deshecha',
    LEASE_COMMENTS_CONVERTED: 'Comentario heredado convertido en nota',
    LEASE_ANTICIPATED_ACCESSORY_SET: 'Accesorio por vencer en letra anticipada',
    LEASE_ANTICIPATED_REVERTED: 'Letra anticipada regresada a pendiente',
    //* B-004: «Copiar del cliente» en Algoritmo (`metadata`: before/after con fecha, oferta y si venía vacío).
    LEASE_VERIFICATION_COPIED_FROM_CLIENT: 'Análisis fiscal copiado del cliente',
  }

  /**
   * Movimientos de dinero que la API sella en el auditTrail del arrendamiento al aplicar, cancelar,
   * anular o fechar un pago. Se pintan distinto porque su «valor» es un monto y unas letras, no un
   * antes/después de cartera, y NO se deshacen desde aquí: su reverso es otra operación de pagos.
   */
  private readonly paymentActions: Record<string, { icon: string; tone: string }> = {
    LEASE_PAYMENT_APPLIED: { icon: 'fa-hand-holding-dollar', tone: 'is-positive' },
    LEASE_APPLICATION_CANCELLED: { icon: 'fa-rotate-left', tone: 'is-neutral' },
    LEASE_PAYMENT_VOIDED: { icon: 'fa-ban', tone: 'is-danger' },
    LEASE_PAYMENT_DATE_CHANGED: { icon: 'fa-calendar-day', tone: 'is-info' },
    LEASE_PAYMENT_AMOUNT_CORRECTED: { icon: 'fa-scale-balanced', tone: 'is-info' },
  }

  /**
   * Cambios de un valor que no son saldos de cartera ni movimientos de un pago: el estado que deja la
   * sincronización con la tabla y lo que escriben los guiones de saneamiento de la migración (residual,
   * condonación de terminaciones, comentarios, letras anticipadas). Se pintan como antes → después con su
   * propio ícono y SIN «Deshacer»: el estado se deriva de los saldos y lo de un guion lo revierte el guion.
   * `perPeriod` agrega la letra a la pista, porque esas entradas se escriben una por letra.
   */
  private readonly valueActions: Record<string, { icon: string; tone: string; hint: string; perPeriod?: boolean }> = {
    LEASE_STATUS_CHANGED: { icon: 'fa-flag-checkered', tone: 'is-info', hint: 'estado del arrendamiento' },
    LEASE_RESIDUAL_CORRECTED: { icon: 'fa-scale-balanced', tone: 'is-neutral', hint: 'residual (subtotal + IVA = total)' },
    LEASE_WRITE_OFF_RECORDED: { icon: 'fa-file-circle-check', tone: 'is-neutral', hint: 'saldo condonado por la terminación' },
    LEASE_WRITE_OFF_REVERTED: { icon: 'fa-rotate-left', tone: 'is-neutral', hint: 'saldo condonado por la terminación' },
    LEASE_COMMENTS_CONVERTED: { icon: 'fa-comment-dots', tone: 'is-neutral', hint: 'comentarios' },
    LEASE_ANTICIPATED_ACCESSORY_SET: { icon: 'fa-calendar-plus', tone: 'is-info', hint: 'letra anticipada', perPeriod: true },
    LEASE_ANTICIPATED_REVERTED: { icon: 'fa-rotate-left', tone: 'is-neutral', hint: 'letra anticipada', perPeriod: true },
    LEASE_VERIFICATION_COPIED_FROM_CLIENT: { icon: 'fa-copy', tone: 'is-info', hint: 'análisis fiscal del arrendamiento' },
  }

  private readonly applicationTypeLabels: Record<string, string> = {
    AMORTIZATION_PERIOD: 'renta',
    FIRST_PAYMENT: 'pago inicial',
    RESIDUAL_PAYMENT: 'residual',
  }

  /** Entrada que se está deshaciendo, para bloquear su botón mientras corre. */
  reverting: string | null = null

  /** Entradas cuyo detalle por letra se puede desplegar. */
  expanded: Record<string, boolean> = {}

  // Gestión de comentarios
  addCommentDialog = false
  newCommentValue = ''
  commentError = ''
  currentUser: IUserAuthenticated | null = null

  private subscriptions: Subscription[] = []

  constructor(
    public leaseDetailService: LeaseDetailService,
    public commentsService: CommentsService,
    private messageService: MessageService,
    private confirmationService: ConfirmationService,
  ) {}

  ngOnInit(): void {
    this.loadCurrentUser()
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((sub) => sub.unsubscribe())
  }

  // ===== Auditoría =====

  // Entradas de auditoría, más recientes primero
  get auditEntries(): any[] {
    const entries = this.leaseDetailService.data?.auditTrail || []
    return [...entries].sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
  }

  actionLabel(action: string): string {
    return this.actionLabels[action] || action
  }

  authorName(user: any): string {
    if (!user) return 'Sistema'
    if (typeof user === 'string') return 'Usuario'
    return `${user.names || ''} ${user.lastNames || ''}`.trim() || 'Usuario'
  }

  // ── Ediciones de montos ──

  /** Las ediciones de montos comparan valor de cartera, así que su antes/después va como moneda. */
  /**
   * Una entrada se puede deshacer si es una edición de saldos, no es ella misma una reversión, y
   * nadie la deshizo ya. El backend revalida lo mismo: esto solo evita ofrecer un botón que fallaría.
   */
  canRevert(entry: any): boolean {
    if (!this.isAmountsEntry(entry)) return false
    if (entry.action === 'LEASE_AMOUNTS_REVERT') return false
    return !this.entries.some((e: any) => e.action === 'LEASE_AMOUNTS_REVERT' && String(e.metadata?.revertOf) === String(entry._id))
  }

  /** La entrada que deshizo a ésta, para poder señalarlo en el timeline. */
  revertedBy(entry: any): any {
    return this.entries.find((e: any) => e.action === 'LEASE_AMOUNTS_REVERT' && String(e.metadata?.revertOf) === String(entry._id)) || null
  }

  private get entries(): any[] {
    return ((this.leaseDetailService.data as any)?.auditTrail || []) as any[]
  }

  // ===== Deshacer una edición de saldos =====
  //
  //! Antes se previsualizaba el REVERT y se aplicaba confirmando TODAS sus advertencias bloqueantes sin
  //! enseñarlas: deshacer una condonación en un arrendamiento liquidado lo reactivaba sin que nadie viera
  //! LEASE_WILL_REACTIVATE ni LEASE_NOT_ACTIVE. Ahora «Deshacer» abre un diálogo con la vista previa —el
  //! cambio de estado y cada advertencia con su casilla— y se aplica solo con lo que el usuario confirmó.

  readonly warningLabels = APPLICATION_WARNING_LABELS

  /** Entrada que se está por deshacer: abre el diálogo. */
  revertEntry: any = null
  revertPreview: any = null
  revertPreviewError = ''
  revertLoading = false
  revertConfirmations: Record<string, boolean> = {}

  get showRevertDialog(): boolean {
    return !!this.revertEntry
  }

  get revertMessage(): string {
    if (!this.revertEntry) return ''
    const n = this.periodDiffs(this.revertEntry).length
    return `Se devolverán los montos al valor que tenían antes de «${this.actionLabel(this.revertEntry.action)}»${n ? `, en ${n} letra(s)` : ''}. La entrada original se conserva y esta reversión queda registrada.`
  }

  get revertBlockingWarnings(): any[] {
    return (this.revertPreview?.warnings || []).filter((w: any) => w.blocking)
  }

  get canConfirmRevert(): boolean {
    return !!this.revertPreview && !this.reverting && this.revertBlockingWarnings.every((w: any) => this.revertConfirmations[w.code])
  }

  confirmRevert(entry: any): void {
    this.revertEntry = entry
    this.revertPreview = null
    this.revertPreviewError = ''
    this.revertConfirmations = {}
    this.revertLoading = true
    this.leaseDetailService.previewLeaseAmounts({ operation: 'REVERT', auditEntryId: entry._id }).subscribe((prev) => {
      this.revertLoading = false
      if (prev.error) {
        this.revertPreviewError = prev.msg || 'No se pudo calcular cómo quedaría el arrendamiento.'
        return
      }
      this.revertPreview = prev.data
    })
  }

  closeRevert(): void {
    if (this.reverting) return
    this.revertEntry = null
    this.revertPreview = null
    this.revertPreviewError = ''
    this.revertConfirmations = {}
  }

  applyRevert(): void {
    if (!this.canConfirmRevert) return
    const entry = this.revertEntry
    this.reverting = String(entry._id)
    const payload = {
      operation: 'REVERT',
      auditEntryId: entry._id,
      reason: `Reversión de: ${this.actionLabel(entry.action)}`,
      stateHash: this.revertPreview?.stateHash,
      //* Solo lo que el usuario marcó: nunca se confirma por él una advertencia que no vio.
      confirmations: Object.keys(this.revertConfirmations).filter((k) => this.revertConfirmations[k]),
    }
    this.leaseDetailService.applyLeaseAmounts(payload).subscribe((r) => {
      this.reverting = null
      if (r.error) {
        this.messageService.add({ severity: 'error', summary: 'No se pudo deshacer', detail: r.msg, life: 8000 })
        return
      }
      this.closeRevert()
      this.messageService.add({ severity: 'success', summary: 'Edición deshecha', detail: 'Los montos volvieron a su valor anterior.', life: 5000 })
    })
  }

  isPaymentEntry(entry: any): boolean {
    return !!this.paymentActions[entry?.action]
  }

  isDateChangeEntry(entry: any): boolean {
    return entry?.action === 'LEASE_PAYMENT_DATE_CHANGED'
  }

  /** B-012 · Se pinta como antes → después del monto, no como un movimiento de dinero hacia unas letras. */
  isAmountCorrectionEntry(entry: any): boolean {
    return entry?.action === 'LEASE_PAYMENT_AMOUNT_CORRECTED'
  }

  markerIcon(entry: any): string {
    return this.paymentActions[entry?.action]?.icon || this.valueActions[entry?.action]?.icon || 'fa-pen-to-square'
  }

  markerTone(entry: any): string {
    return this.paymentActions[entry?.action]?.tone || this.valueActions[entry?.action]?.tone || ''
  }

  /** Qué valor cambió, bajo el antes → después: sin esto «Activo → Liquidado» no dice de qué. */
  valueHint(entry: any): string {
    const config = this.valueActions[entry?.action]
    if (!config) return ''
    const period = entry?.metadata?.period
    return config.perPeriod && period != null ? `${config.hint} · letra ${period}` : config.hint
  }

  applicationTypeLabel(type: string): string {
    return this.applicationTypeLabels[type] || ''
  }

  /** «letra 3», «letras 3-4», «letras 1, 3, 5-7»: las corridas consecutivas se juntan en un rango. */
  formatPeriods(periods: any): string {
    const ns = [...new Set((Array.isArray(periods) ? periods : []).map(Number).filter((n) => Number.isFinite(n)))].sort((a, b) => a - b)
    if (!ns.length) return ''
    const tramos: string[] = []
    let inicio = ns[0]
    let previo = ns[0]
    for (const n of [...ns.slice(1), NaN]) {
      if (n === previo + 1) {
        previo = n
        continue
      }
      tramos.push(inicio === previo ? `${inicio}` : `${inicio}-${previo}`)
      inicio = n
      previo = n
    }
    return `${ns.length === 1 ? 'letra' : 'letras'} ${tramos.join(', ')}`
  }

  //* El cambio de estado se pinta con el color canónico de cada estado, como en la lista de arrendamientos y en la
  //* vista previa del editor. En rojo → verde, «Liquidado → Activo» se leía como una buena noticia.
  isStatusEntry(entry: any): boolean {
    return entry?.action === 'LEASE_STATUS_CHANGED'
  }

  statusSeverity(status: any): string {
    return (LEASE_STATUS_COLOR_ENUM as Record<string, string>)[String(status ?? '')] ?? 'secondary'
  }

  isAmountsEntry(entry: any): boolean {
    return typeof entry?.action === 'string' && entry.action.startsWith('LEASE_AMOUNTS_')
  }

  /** La tasa moratoria se guarda como decimal (0.035) y leerla así en la bitácora obliga a traducir
   *  mentalmente: se pinta como el porcentaje que se capturó. */
  isRateEntry(entry: any): boolean {
    return entry?.action === 'MORATORY_RATE_CHANGED'
  }

  formatRate(v: any): string {
    const n = Number(v)
    if (v === null || v === undefined || isNaN(n)) return '—'
    return `${new Intl.NumberFormat('es-MX', { minimumFractionDigits: 1, maximumFractionDigits: 2 }).format(n * 100)} %`
  }

  formatMoney(v: any): string {
    const n = Number(v)
    if (v === null || v === undefined || isNaN(n)) return '—'
    return new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(n)
  }

  /** Detalle por letra que viaja en `metadata.periods` (una entrada por operación, no una por campo). */
  periodDiffs(entry: any): any[] {
    return entry?.metadata?.periods || []
  }

  toggleDetail(entry: any): void {
    const id = String(entry?._id || '')
    this.expanded[id] = !this.expanded[id]
  }

  isExpanded(entry: any): boolean {
    return !!this.expanded[String(entry?._id || '')]
  }

  fieldLabel(key: string): string {
    const labels: Record<string, string> = {
      capital: 'Capital',
      interest: 'Interés',
      accessoriesAmount: 'Accesorios',
      accessoriesInterest: 'Com. Acc.',
      inputIva: 'IVA',
      rent: 'Pago esperado',
      payment: 'Por pagar',
      appliedPayment: 'Pagado',
      status: 'Estado',
    }
    return labels[key] || key
  }

  private readonly statusLabels: Record<string, string> = { PAID: 'PAGADO', OVERDUE: 'ATRASADO', PENDING: 'PENDIENTE', ANTICIPATED: 'ANTICIPADO' }

  private readonly DIFF_ORDER = ['capital', 'interest', 'accessoriesAmount', 'accessoriesInterest', 'inputIva', 'rent', 'payment', 'appliedPayment', 'status']

  /** Columnas del detalle: la unión de los campos que cambiaron. Igual que en el editor de saldos,
   *  en tabla y no como lista de pares — con varios conceptos la fila envolvía y se partía. */
  diffColumns(entry: any): { key: string; label: string; money: boolean }[] {
    const presentes = new Set<string>()
    for (const d of this.periodDiffs(entry) || []) Object.keys(d?.campos || {}).forEach((k) => presentes.add(k))
    return this.DIFF_ORDER.filter((k) => presentes.has(k)).map((k) => ({ key: k, label: this.fieldLabel(k), money: k !== 'status' }))
  }

  diffCell(diff: any, key: string): { antes: any; despues: any } | null {
    const c = diff?.campos?.[key]
    if (!c) return null
    if (key !== 'status') return c
    return { antes: this.statusLabels[c.antes] || c.antes, despues: this.statusLabels[c.despues] || c.despues }
  }

  /**
   * El resumen de letras afectadas. No todas las acciones son cascadas: `editAmortizationStartDate`
   * sí lo es, pero el editor de saldos edita letras sueltas, limpia saldos o condona residuos, y
   * decir "recalculado en cascada" en esos casos describe mal lo que pasó.
   */
  periodsSummary(entry: any): string {
    const n = entry?.metadata?.periodsAffected || 0
    const plural = n === 1 ? '' : 's'
    // El renumerado no recalcula nada en cascada: reescribe el número de cada letra.
    if (entry?.action === 'LEASE_PERIODS_RENUMBERED') {
      const parciales = entry?.metadata?.rentasParciales || 0
      return `${n} letra${plural} renumerada${plural}${parciales ? ` · ${parciales} eran rentas parciales` : ''}`
    }
    if (entry?.metadata?.scope === 'CASCADE' || !String(entry?.action || '').startsWith('LEASE_AMOUNTS_')) {
      return `${n} período${plural} recalculado${plural} en cascada`
    }
    return `${n} letra${plural} afectada${plural}`
  }

  // Formatea un valor de auditoría: si parece fecha ISO la muestra dd/MM/yyyy (UTC); si no, string.
  formatValue(v: any): string {
    if (v === null || v === undefined || v === '') return '—'
    if (typeof v === 'string' && /^\d{4}-\d{2}-\d{2}/.test(v)) {
      const d = new Date(v)
      if (!isNaN(d.getTime())) return `${String(d.getUTCDate()).padStart(2, '0')}/${String(d.getUTCMonth() + 1).padStart(2, '0')}/${d.getUTCFullYear()}`
    }
    return String(v)
  }

  // ===== Comentarios =====

  private loadCurrentUser(): void {
    const userJson = localStorage.getItem('userJson')
    if (userJson) this.currentUser = JSON.parse(userJson)
  }

  openAddComment(): void {
    this.newCommentValue = ''
    this.commentError = ''
    this.addCommentDialog = true
  }

  cancelAddComment(): void {
    this.addCommentDialog = false
    this.newCommentValue = ''
    this.commentError = ''
  }

  addComment(): void {
    this.commentError = ''

    if (!this.leaseDetailService.id) {
      this.commentError = 'No se encontró el ID del arrendamiento'
      return
    }

    const subscription = this.commentsService.createComment(this.leaseDetailService.id, this.newCommentValue).subscribe({
      next: (result) => {
        if (result.success) {
          this.messageService.add({ severity: 'success', summary: 'Éxito', detail: result.message })
          this.addCommentDialog = false
          this.newCommentValue = ''
        } else {
          this.commentError = result.message
          this.messageService.add({ severity: 'error', summary: 'Error', detail: result.message })
        }
      },
      error: (error) => {
        console.error('Error al agregar comentario:', error)
        this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Error de conexión al agregar comentario' })
      },
    })

    this.subscriptions.push(subscription)
  }

  confirmDeleteComment(commentId: string): void {
    this.confirmationService.confirm({
      message: '¿Está seguro de que desea eliminar este comentario? Esta acción no se puede deshacer.',
      header: 'Confirmar eliminación',
      icon: 'pi pi-exclamation-triangle',
      accept: () => this.deleteComment(commentId),
    })
  }

  private deleteComment(commentId: string): void {
    if (!this.leaseDetailService.id) {
      this.messageService.add({ severity: 'error', summary: 'Error', detail: 'No se pudo obtener la información necesaria para eliminar el comentario' })
      return
    }

    const subscription = this.commentsService.deleteComment(this.leaseDetailService.id, commentId, this.currentUser?.user?.id || 'unknown').subscribe({
      next: (result) => {
        const severity = result.success ? 'success' : 'error'
        this.messageService.add({ severity, summary: result.success ? 'Éxito' : 'Error', detail: result.message })
      },
      error: (error) => {
        console.error('Error al eliminar comentario:', error)
        this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Error de conexión al eliminar comentario' })
      },
    })

    this.subscriptions.push(subscription)
  }
}
